import { useRecoilState, useRecoilValue } from 'recoil';
import { useHippoText } from './useHippoText';
import usePageNavigation from './usePageNavigation';
import { callMeVariantOpcAtom } from '../recoil/atoms/callMeVariantOpcAtom';
import { opcInfoAtom } from '../recoil/atoms/opcInfoAtom';
import type { CallMeVariant } from '../recoil/types/opcTypes';
import useOpcJob from './useOpcJob';

interface CallMeModalProps {
    title?: string;
    description?: string;
    activityCode: string;
    isDefault: boolean;
    closeModal: () => void;
    closeConfirmationModal: () => void;
    overlapseModalText?: Record<string, string>;
    showModal: boolean;
}

const useOPCCallMeModal = (reOpenOPCModal: () => void): { callMeProps?: CallMeModalProps } => {
    const getHippoText = useHippoText();
    const { withdrawCurrentOPCShoppingBasket } = useOpcJob();
    const { goBackToDetailsPage, goToShoppingBasket } = usePageNavigation();
    const [callMeVariant, setCallMeVariant] = useRecoilState(callMeVariantOpcAtom);
    const { isFromDAFlow } = useRecoilValue(opcInfoAtom);

    if (!callMeVariant) return { callMeProps: undefined };

    const backToPreviousPage = (): void => {
        setCallMeVariant(undefined);
        if (isFromDAFlow) {
            goToShoppingBasket();
        } else {
            void withdrawCurrentOPCShoppingBasket();
            goBackToDetailsPage();
        }
    };

    const commonCloseCallMe = (): void => {
        setCallMeVariant(undefined);
        reOpenOPCModal();
    };

    const defaultProps: CallMeModalProps = {
        title: undefined,
        description: undefined,
        activityCode: 'dss_call_request',
        isDefault: false,
        closeModal: () => {
            setCallMeVariant(undefined);
        },
        closeConfirmationModal: backToPreviousPage,
        overlapseModalText: undefined,
        showModal: !!callMeVariant
    };

    const modalPropsMap: Record<Exclude<CallMeVariant, undefined>, CallMeModalProps> = {
        default: defaultProps,
        customerStopModal: {
            ...defaultProps,
            closeModal: backToPreviousPage
        },
        voluntaryAlarmNoToYes: {
            ...defaultProps,
            title: getHippoText('DSS-CALLME-MODAL-PRC-ALARM-HEADER'),
            description: getHippoText('DSS-CALLME-MODAL-PRC-ALARM-SUBHEADER'),
            closeModal: backToPreviousPage
        },
        injuryDegree: {
            ...defaultProps,
            title: getHippoText('DSS-ADD-SIMPLE-COVERAGE-INJURY-DEGREE-SUMINSURED-CALL-ME-HEADER'),
            description: getHippoText('DSS-ADD-SIMPLE-COVERAGE-INJURY-DEGREE-SUMINSURED-CALL-ME'),
            overlapseModalText: {
                modalChooseTimeSubHeader:
                    'Vælg et tidspunkt, så ringer vi dig op og ordner det over telefonen.'
            },
            closeModal: commonCloseCallMe
        },
        employmentNotInList: {
            ...defaultProps,
            title: getHippoText('DSS-EMPLOYMENT-NOT-ON-LIST-CALL-ME-HEADER'),
            description: getHippoText('DSS-EMPLOYMENT-NOT-ON-LIST-CALL-ME-SUBHEADER'),
            closeModal: commonCloseCallMe
        },
        hazardAboveFiveEmployment: {
            ...defaultProps,
            title: getHippoText('DSS-EMPLOYMENTHAZARD-CALL-ME-HEADER'),
            description: getHippoText('DSS-EMPLOYMENTHAZARD-CALL-ME'),
            closeModal: commonCloseCallMe
        },
        professionalSportEmployment: {
            ...defaultProps,
            title: getHippoText('DSS-ADD-SIMPLE-COVERAGE-HIGH-RISK-SPORT-TITLE-CALL-ME'),
            description: getHippoText(
                'DSS-ADD-SIMPLE-COVERAGE-ACCIMMEDIATECOMPENSATIONCOV-SUMINSURED-CALL-ME'
            ),
            closeModal: backToPreviousPage
        },
        professionalSportEmploymentAddImmediateComp: {
            ...defaultProps,
            title: getHippoText('DSS-ADD-SIMPLE-COVERAGE-HIGH-RISK-SPORT-TITLE-CALL-ME'),
            description: getHippoText(
                'DSS-ADD-SIMPLE-COVERAGE-ACCIMMEDIATECOMPENSATIONCOV-SUMINSURED-CALL-ME'
            ),
            closeModal: backToPreviousPage
        },
        extendedToStandard: {
            ...defaultProps,
            title: getHippoText('DSS-ADD-SIMPLE-COVERAGE-CONTRACT-TYPE-SUMINSURED-CALL-ME-HEADER'),
            description: getHippoText('DSS-ADD-SIMPLE-COVERAGE-CONTRACT-TYPE-SUMINSURED-CALL-ME'),
            closeModal: commonCloseCallMe
        },
        fulltimeToLeisure: {
            ...defaultProps,
            title: getHippoText(
                'DSS-ADD-SIMPLE-COVERAGE-FULLTIME-OR-LEISURE-SUMINSURED-CALL-ME-HEADER'
            ),
            description: getHippoText(
                'DSS-ADD-SIMPLE-COVERAGE-FULLTIME-OR-LEISURE-SUMINSURED-CALL-ME'
            ),
            closeModal: commonCloseCallMe
        },
        traNumberInHousehold: {
            ...defaultProps,
            title: getHippoText('DSS-CALL-ME-MODAL-DOWNGRADE-TRAVEL-TITLE'),
            description: getHippoText('DSS-CALL-ME-MODAL-DOWNGRADE-TRAVEL-DESCRIPTION'),
            overlapseModalText: {
                modalChooseTimeSubHeader: '',
                modalContactedPrompt: 'Hvornår skal vi ringe?'
            },
            closeModal: backToPreviousPage
        },
        childTurn18Within40days: {
            ...defaultProps,
            title: 'Bestil et opkald',
            description: getHippoText('DSS-ADDITIONAL-INFORMATION- CHILD-AGE-VALIDATION'),
            closeModal: backToPreviousPage
        },
        youngTurn28Within40days: {
            ...defaultProps,
            title: 'Bestil et opkald',
            description: getHippoText('DSS-ACC-YOUNG-TOO-OLD-CALL-ME'),
            closeModal: backToPreviousPage
        },
        youthContentTurn28Within40days: {
            ...defaultProps,
            title: 'Bestil et opkald',
            description: getHippoText('DSS-YOUTH-CONTENT-TOO-OLD-CALL-ME'),
            closeModal: backToPreviousPage
        },
        deathCovExceedLimit: {
            ...defaultProps,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            title: 'Bestil et opkald',
            description: getHippoText('DSS-ADD-SIMPLE-COVERAGE-ACC-DEATH-COV-SUMINSURED-CALL-ME'),
            closeModal: backToPreviousPage
        },
        addLeasingCarReturnCov: {
            ...defaultProps,
            title: getHippoText('DSS-CALL-ME-MODAL-MVP-COV-11-HEADER'),
            description: getHippoText('DSS-CALL-ME-MODAL-MVP-COV-11-SUBHEADER'),
            closeModal: backToPreviousPage
        },
        addDeathCov: {
            ...defaultProps,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            title: 'Du kan ikke købe dækningen dødsfaldsdækningen online',
            description: getHippoText('DSS-ADD-SIMPLE-COVERAGE-ACCDEATHCOV-SUMINSURED-CALL-ME'),
            closeModal: backToPreviousPage
        },
        addHighRiskOrImmediateCompCov: {
            ...defaultProps,
            title: getHippoText('DSS-ADD-SIMPLE-COVERAGE-HIGH-RISK-SPORT-TITLE-CALL-ME'),
            description: getHippoText('DSS-ADD-SIMPLE-COVERAGE-HIGH-RISK-SPORT-SUMINSURED-CALL-ME'),
            closeModal: backToPreviousPage
        },
        lowerAccInsuranceSumThanPrevious: {
            ...defaultProps,
            title: getHippoText('DSS-ADD-SIMPLE-COVERAGE-SUMINSURED-CALL-ME-HEADER'),
            description: getHippoText('DSS-ADD-SIMPLE-COVERAGE-SUMINSURED-CALL-ME'),
            closeModal: backToPreviousPage
        },
        petBetween5and7YearsOldHealth: {
            ...defaultProps,
            title: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-HEADER'),
            description: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-SUBHEADER'),
            closeModal: backToPreviousPage
        },
        petBetween5and7YearsOldHealthPlus: {
            ...defaultProps,
            title: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-HEADER'),
            description: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-SUBHEADER'),
            closeModal: backToPreviousPage
        },
        petBetween5and7YearsOldLife: {
            ...defaultProps,
            title: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-HEADER'),
            description: getHippoText('DSS-CALL-ME-MODAL-OLDER-PET-SUBHEADER'),
            closeModal: backToPreviousPage
        },
        prcInsuraceSumsTooLow: {
            ...defaultProps,
            title: getHippoText('DSS-CALLME-MODAL-INSURANCESUMS-PRC-HEADER'),
            description: getHippoText('DSS-CALLME-MODAL-INSURANCESUMS-PRC-BODY'),
            closeModal: backToPreviousPage
        },
        carForcedDeductible: {
            ...defaultProps,
            closeModal: backToPreviousPage
        },
        prhPrimaryHeating: {
            ...defaultProps,
            closeModal: commonCloseCallMe
        },
        uvpStationary: {
            ...defaultProps,
            title: 'Vi har brug for at tale med dig',
            description: getHippoText('DSS-CALL-ME-MODAL-CARAVAN-STATIONARY'),
            closeModal: backToPreviousPage
        }
    };

    return { callMeProps: modalPropsMap[callMeVariant] };
};

export { useOPCCallMeModal };
